import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import {
  createTemplateFromReportMutation,
  CreateTemplateFromReportInput,
  createFutrliTemplateMutation,
  CreateFutrliTemplateInput
} from '../../../mutations/templates';
import {
  deleteTemplateMutation,
  DeleteTemplateInput,
  duplicateTemplateMutation,
  DuplicateTemplateInput,
  publishTemplateMutation,
  PublishTemplateInput
} from '../../../mutations/reportActions';

export interface ModifyReportTemplateViewState extends ApiViewState {
  duplicatedTemplateId?: string;
  errorCreatingTemplate: Error | null;
  errorDeletingTemplate: Error | null;
  errorDuplicatingTemplate: Error | null;
  errorPublishingTemplate: Error | null;
  isCreatingTemplate: boolean;
  isDeletingTemplate: boolean;
  isDuplicatingTemplate: boolean;
  isPublishingTemplate: boolean;
  newTemplateId?: string;
}

export class ModifyReportTemplateView extends ApiView {
  private createTemplateMutation: ReturnType<typeof createTemplateFromReportMutation>;
  private createFutrliTemplateMutation: ReturnType<typeof createFutrliTemplateMutation>;
  private deleteTemplateMutation: ReturnType<typeof deleteTemplateMutation>;
  private duplicateTemplateMutation: ReturnType<typeof duplicateTemplateMutation>;
  private publishTemplateMutation: ReturnType<typeof publishTemplateMutation>;

  public state: ModifyReportTemplateViewState = {
    ...getDefaultApiViewState(),
    duplicatedTemplateId: undefined,
    errorCreatingTemplate: null,
    errorDeletingTemplate: null,
    errorDuplicatingTemplate: null,
    errorPublishingTemplate: null,
    isCreatingTemplate: false,
    isDeletingTemplate: false,
    isDuplicatingTemplate: false,
    isPublishingTemplate: false,
    newTemplateId: undefined
  };

  constructor(workspaceId: string, organisationId: string, isConsolidation = false) {
    super();

    this.createTemplateMutation = this.addMutation(
      createTemplateFromReportMutation(workspaceId, organisationId, isConsolidation)
    );
    this.createFutrliTemplateMutation = this.addMutation(
      createFutrliTemplateMutation(workspaceId, organisationId, isConsolidation)
    );
    this.deleteTemplateMutation = this.addMutation(
      deleteTemplateMutation(workspaceId, organisationId, isConsolidation)
    );
    this.duplicateTemplateMutation = this.addMutation(
      duplicateTemplateMutation(workspaceId, organisationId, isConsolidation)
    );
    this.publishTemplateMutation = this.addMutation(
      publishTemplateMutation(workspaceId, organisationId, isConsolidation)
    );

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************
  public createTemplateFromReport(data: CreateTemplateFromReportInput) {
    return this.createTemplateMutation.mutate(data);
  }

  public createFutrliTemplate(data: CreateFutrliTemplateInput) {
    return this.createFutrliTemplateMutation.mutate(data);
  }

  public deleteTemplate(data: DeleteTemplateInput) {
    return this.deleteTemplateMutation.mutate(data);
  }

  public duplicateTemplate(data: DuplicateTemplateInput) {
    return this.duplicateTemplateMutation.mutate(data);
  }

  public publishTemplate(data: PublishTemplateInput) {
    return this.publishTemplateMutation.mutate(data);
  }

  public resetMutations() {
    this.createTemplateMutation.reset();
    this.createFutrliTemplateMutation.reset();
    this.deleteTemplateMutation.reset();
    this.duplicateTemplateMutation.reset();
    this.publishTemplateMutation.reset();
    this.updateState();
  }

  // ****************************
  // * Private / Protected API *
  // ****************************
  protected getNewState(): ModifyReportTemplateViewState {
    return {
      ...super.getNewState(),
      duplicatedTemplateId: this.duplicateTemplateMutation.getCurrentResult().data?.id,
      errorCreatingTemplate:
        this.createTemplateMutation.getCurrentResult().error ??
        this.createFutrliTemplateMutation.getCurrentResult().error,
      errorDeletingTemplate: this.deleteTemplateMutation.getCurrentResult().error,
      errorDuplicatingTemplate: this.duplicateTemplateMutation.getCurrentResult().error,
      errorPublishingTemplate: this.publishTemplateMutation.getCurrentResult().error,
      isCreatingTemplate:
        this.createTemplateMutation.getCurrentResult().status === 'pending' ||
        this.createFutrliTemplateMutation.getCurrentResult().status === 'pending',
      isDeletingTemplate: this.deleteTemplateMutation.getCurrentResult().status === 'pending',
      isDuplicatingTemplate: this.duplicateTemplateMutation.getCurrentResult().status === 'pending',
      isPublishingTemplate: this.publishTemplateMutation.getCurrentResult().status === 'pending',
      newTemplateId:
        this.createTemplateMutation.getCurrentResult().data?.id ??
        this.createFutrliTemplateMutation.getCurrentResult().data?.id
    };
  }
}
